import { extractCellText } from './helpers';

// CSV-only "group split" for nested tables (the org-group dashboards). On screen
// a row's group is shown by indentation — e.g. an Organisation with its Floors
// nested under it — but a flat CSV loses that. When the component sets
// `csv_group_header` (and optionally `csv_detail_header`) the export:
//
//   * prepends a hidden column naming the group each row belongs to
//     (e.g. "Organisation"),
//   * retitles the first column for the download to name the detail rows
//     (e.g. "Floor") and blanks it on group/summary rows, and
//   * keeps only the detail rows in the file (see `csvDetailRows`).
//
// Display is unchanged — everything here only affects `download('csv', ...)`.
// The controller just composes these helpers.

// The first data column's field, descending through any column-group wrappers.
// Grouped tables (e.g. Overview) wrap the first column in an empty header
// group, so `columns[0]` may be a group rather than the column itself.
export function firstColumnField(columns) {
  const first = columns[0];
  if (!first) return undefined;
  return Array.isArray(first.columns)
    ? firstColumnField(first.columns)
    : first.field;
}

// Apply the group split to already-built Tabulator columns: retitle the first
// (detail) column for download and prepend the group column. Returns the new
// column list (the first column is annotated in place, which is safe — these
// are freshly built config objects).
export function withGroupColumn(columns, { groupHeader, detailHeader }) {
  const field = firstColumnField(columns);
  renameDetailColumn(columns, field, detailHeader);
  return [groupColumn(groupHeader, field), ...columns];
}

// Rows for a grouped CSV download: each active top-level row plus all its
// descendants (Tabulator materialises collapsed children on demand), minus
// decorative heading rows and group/summary rows (any row with children) — so
// the file is a clean flat list of detail rows. Call with the Tabulator table.
export function csvDetailRows(table) {
  const collect = (row) => [row, ...row.getTreeChildren().flatMap(collect)];
  return table
    .getRows('active')
    .flatMap(collect)
    .filter(
      (row) => !row.getData()._heading && row.getTreeChildren().length === 0
    );
}

// The first column is the detail level (e.g. Floor). On group/summary rows it
// repeats the group column, so blank it there; retitle it to the detail header
// for the download only. Matched by field (unique per table) so it works
// whether or not the column is nested in a header group.
function renameDetailColumn(columns, field, detailHeader) {
  columns.forEach((col) => {
    if (Array.isArray(col.columns)) {
      renameDetailColumn(col.columns, field, detailHeader);
    } else if (col.field === field) {
      if (detailHeader) col.titleDownload = detailHeader;
      col.accessorDownload = (value, _data, _type, _params, _col, row) =>
        row.getTreeParent && row.getTreeParent() ? extractCellText(value) : '';
    }
  });
}

// A download-only, screen-hidden column naming each row's top-level group. The
// on-screen indentation conveys this, but a flat CSV needs it in a column.
// Prefers an explicit `data-group-label` (e.g. a group header cell that also
// shows a "(count)" suffix we don't want) over the first cell's text.
function groupColumn(title, field) {
  return {
    title,
    field: '__csvGroup',
    visible: false,
    download: true,
    headerSort: false,
    accessorDownload: (_value, _data, _type, _params, _col, row) => {
      let group = row;
      let parent = group.getTreeParent && group.getTreeParent();
      while (parent) {
        group = parent;
        parent = group.getTreeParent && group.getTreeParent();
      }
      const groupData = group.getData();
      const label = groupData._dataAttrs && groupData._dataAttrs.groupLabel;
      return label ?? extractCellText(field ? groupData[field] ?? '' : '');
    },
  };
}
